import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { GalaxyCheckboxModule } from '@vendasta/galaxy/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { HostUser } from '@vendasta/meetings';
import { TranslateModule } from '@ngx-translate/core';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { POSTHOG_KEYS, POSTHOG_CATEGORIES, POSTHOG_ACTIONS } from '../../constants';

export interface TeamMemberFilterDialogData {
  teamMembers: HostUser[];
  selectedUserIds: string[];
  businessId: string;
  tab: 'upcoming' | 'past';
}

@Component({
  selector: 'meeting-scheduler-team-member-filter-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogModule,
    ReactiveFormsModule,
    FormsModule,
    GalaxyCheckboxModule,
    MatButtonModule,
    MatInputModule,
    GalaxyFormFieldModule,
    TranslateModule,
  ],
  templateUrl: './team-member-filter-dialog.component.html',
  styleUrl: './team-member-filter-dialog.component.scss',
})
export class TeamMemberFilterDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<TeamMemberFilterDialogComponent>);
  private readonly analyticsService = inject(ProductAnalyticsService);
  readonly data = inject<TeamMemberFilterDialogData>(MAT_DIALOG_DATA);

  searchControl = new FormControl('', { nonNullable: true });
  readonly searchText = toSignal(this.searchControl.valueChanges, { initialValue: '' });
  private readonly initialIds = new Set<string>(this.data.selectedUserIds);
  private readonly selectedIds = signal(new Set<string>(this.data.selectedUserIds));

  hasChanges = computed(() => {
    const current = this.selectedIds();
    if (current.size !== this.initialIds.size) return true;
    return [...current].some((id) => !this.initialIds.has(id));
  });

  isEmpty = computed(() => this.selectedIds().size === 0);

  filteredMembers = computed(() => {
    const search = this.searchText().toLowerCase();
    return search
      ? this.data.teamMembers.filter((m) => m.displayName.toLowerCase().includes(search))
      : this.data.teamMembers;
  });

  isAllSelected = computed(() => {
    const ids = this.selectedIds();
    return this.data.teamMembers.length > 0 && this.data.teamMembers.every((m) => ids.has(m.userId));
  });

  isSelected(userId: string): boolean {
    return this.selectedIds().has(userId);
  }

  onSelectAllChecked(checked: boolean): void {
    if (checked && this.isAllSelected()) return;
    if (!checked && !this.isAllSelected()) return;
    if (checked) {
      this.selectedIds.set(new Set(this.data.teamMembers.map((m) => m.userId)));
    } else {
      this.selectedIds.set(new Set());
    }
  }

  onMemberChecked(userId: string, checked: boolean): void {
    if (checked === this.selectedIds().has(userId)) return;
    const next = new Set(this.selectedIds());
    if (checked) {
      next.add(userId);
    } else {
      next.delete(userId);
    }
    this.selectedIds.set(next);
    this.analyticsService.trackEvent(
      POSTHOG_KEYS.TEAM_MEMBER_FILTER_USER_SELECTED,
      POSTHOG_CATEGORIES.USER,
      POSTHOG_ACTIONS.CLICK,
      undefined,
      {
        business_id: this.data.businessId,
        action: checked ? 'selected' : 'deselected',
        tab: this.data.tab,
      },
    );
  }

  apply(): void {
    this.dialogRef.close(Array.from(this.selectedIds()));
  }
}
